<script setup lang="ts">
import { ChevronRight } from "@lucide/vue";
import {
  useIntervalFn,
  usePreferredReducedMotion,
  useScrollLock,
} from "@vueuse/core";
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  shallowRef,
  watch,
} from "vue";
import { useI18n } from "../composables/useI18n";
import type {
  EditorTourConfig,
  EditorTourStartOptions,
  EditorTourStep,
  EditorTourStepPlacement,
} from "../types/editor-tour";
import {
  clearTourDismissedState,
  readTourDismissedState,
  writeTourDismissedState,
} from "../utils/tourStorage";

const props = withDefaults(
  defineProps<{
    tourConfig: EditorTourConfig;
    /**
     * Align tooltip palette with the editor chrome (Teleport is outside `.tpl`,
     * so theme CSS variables do not apply unless we mirror light/dark here).
     */
    darkMode?: boolean;
  }>(),
  { darkMode: false },
);

const { t, format } = useI18n();

const tourActive = ref(false);
const stepIndex = ref(0);
const targetRect = ref<DOMRect | null>(null);
const typedText = ref("");
const prefersReducedMotion = usePreferredReducedMotion();
const activeSteps = shallowRef<EditorTourStep[]>([]);

const bodyScrollLocked = useScrollLock(document.body);

watch(
  tourActive,
  (active, _was, onCleanup) => {
    bodyScrollLocked.value = active;
    if (!active) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        e.preventDefault();
        dismiss(true);
      }
    };
    document.addEventListener("keydown", onKey, true);
    onCleanup(() => document.removeEventListener("keydown", onKey, true));
  },
  { immediate: true },
);

let typewriterCharIndex = 0;
const { pause: stopTypewriter, resume: resumeTypewriter } = useIntervalFn(
  () => {
    const fullText = currentStepText.value;
    if (typewriterCharIndex < fullText.length) {
      typedText.value = fullText.slice(0, typewriterCharIndex + 1);
      typewriterCharIndex++;
    } else {
      stopTypewriter();
    }
  },
  25,
  { immediate: false },
);

const defaultSteps = computed<EditorTourStep[]>(() => [
  {
    target: ".tpl-body",
    placement: "canvas",
    title: t.tour.defaults.canvas.title,
    text: t.tour.defaults.canvas.text,
  },
  {
    target: ".tpl-sidebar-rail",
    placement: "sidebar",
    title: t.tour.defaults.sidebar.title,
    text: t.tour.defaults.sidebar.text,
  },
  {
    target: ".tpl-right-sidebar",
    placement: "rightSidebar",
    title: t.tour.defaults.rightSidebar.title,
    text: t.tour.defaults.rightSidebar.text,
  },
]);

function filterResolvableSteps(steps: EditorTourStep[]): EditorTourStep[] {
  return steps.filter((s) => {
    const el = document.querySelector(s.target);
    if (el) return true;
    return s.skipIfMissing === false;
  });
}

const currentStep = computed(() => activeSteps.value[stepIndex.value] ?? null);

const currentStepTitle = computed(() => currentStep.value?.title ?? "");
const currentStepText = computed(() => currentStep.value?.text ?? "");

function resolvePlacement(
  step: EditorTourStep | null,
): EditorTourStepPlacement {
  return step?.placement ?? "below";
}

function updateTargetRect(): void {
  const step = currentStep.value;
  if (!step) {
    targetRect.value = null;
    return;
  }
  const el = document.querySelector<HTMLElement>(step.target);
  if (!el) {
    targetRect.value = null;
    return;
  }
  const placement = resolvePlacement(step);
  if (placement === "below") {
    el.scrollIntoView({
      behavior: "instant",
      block: "nearest",
      inline: "nearest",
    });
  }
  targetRect.value = el.getBoundingClientRect();
}

function startTypewriter(): void {
  stopTypewriter();
  const fullText = currentStepText.value;
  typedText.value = "";
  if (prefersReducedMotion.value === "reduce") {
    typedText.value = fullText;
    return;
  }
  typewriterCharIndex = 0;
  resumeTypewriter();
}

function buildActiveStepsForRun(): EditorTourStep[] {
  const raw =
    props.tourConfig.steps !== undefined
      ? props.tourConfig.steps
      : defaultSteps.value;
  return filterResolvableSteps(raw);
}

function start(options?: EditorTourStartOptions): void {
  const steps = buildActiveStepsForRun();
  activeSteps.value = steps;
  if (steps.length === 0) {
    tourActive.value = false;
    return;
  }
  const nextIdx = Math.min(
    Math.max(0, options?.stepIndex ?? 0),
    steps.length - 1,
  );
  stepIndex.value = nextIdx;
  tourActive.value = true;
  nextTick(() => {
    updateTargetRect();
    startTypewriter();
  });
}

function dismiss(persist: boolean): void {
  stopTypewriter();
  tourActive.value = false;
  targetRect.value = null;
  if (persist) {
    writeTourDismissedState(props.tourConfig);
  }
}

function nextStep(): void {
  if (stepIndex.value < activeSteps.value.length - 1) {
    stepIndex.value++;
    nextTick(() => {
      updateTargetRect();
      startTypewriter();
    });
  } else {
    dismiss(true);
  }
}

function isDismissed(): boolean {
  return readTourDismissedState(props.tourConfig);
}

function resetDismissed(): void {
  clearTourDismissedState(props.tourConfig);
}

const tooltipStyle = computed(() => {
  const rect = targetRect.value;
  if (!rect) return {};
  const placement = resolvePlacement(currentStep.value);
  const pad = 12;

  if (placement === "canvas") {
    return {
      top: `${rect.top + rect.height / 3}px`,
      left: `${rect.left + rect.width / 2}px`,
      transform: "translate(-50%, -50%)",
    };
  }
  if (placement === "sidebar") {
    return {
      top: `${rect.top + rect.height / 3}px`,
      left: `${rect.right + pad}px`,
    };
  }
  if (placement === "rightSidebar") {
    return {
      top: `${rect.top + rect.height / 3}px`,
      left: `${rect.left - 300 - pad}px`,
    };
  }
  return {
    top: `${rect.bottom + pad}px`,
    left: `${Math.min(rect.left, typeof window !== "undefined" ? window.innerWidth - 320 : rect.left)}px`,
  };
});

const spotlightStyle = computed(() => {
  const rect = targetRect.value;
  if (!rect) return {};
  const placement = resolvePlacement(currentStep.value);
  const pad = 6;
  const isLargePanel =
    placement === "canvas" ||
    placement === "sidebar" ||
    placement === "rightSidebar";
  const w = rect.width + pad * 2;
  const h = rect.height + pad * 2;
  const x = rect.left - pad;
  const y = rect.top - pad;
  return {
    width: `${w}px`,
    height: `${h}px`,
    transform: `translate(${x}px, ${y}px)`,
    borderRadius: isLargePanel ? "14px" : "8px",
  };
});

onMounted(() => {
  if (props.tourConfig.autoStart && !readTourDismissedState(props.tourConfig)) {
    start();
  }
});

onUnmounted(() => {
  stopTypewriter();
  bodyScrollLocked.value = false;
});

defineExpose({
  start,
  dismiss: () => dismiss(true),
  resetDismissed,
  isDismissed,
});
</script>

<template>
  <Teleport to="body">
    <Transition name="tpl-editor-tour">
      <div
        v-if="tourActive"
        class="tpl-editor-tour-overlay tpl:fixed tpl:inset-0 tpl:z-[10001]"
        :class="{ 'tpl-editor-tour-overlay--dark': darkMode }"
        data-testid="editor-tour-overlay"
        @click.self="dismiss(true)"
      >
        <div
          v-if="targetRect"
          data-testid="editor-tour-spotlight"
          class="tpl-editor-tour-spotlight tpl:absolute tpl:top-0 tpl:left-0"
          :style="spotlightStyle"
          @click="dismiss(true)"
        />

        <div
          v-if="targetRect && currentStep"
          role="dialog"
          aria-modal="true"
          :aria-label="currentStepTitle"
          data-testid="editor-tour-tooltip"
          class="tpl-editor-tour-tooltip tpl-editor-tour-tooltip-shell tpl:fixed tpl:z-[10002]"
          :style="tooltipStyle"
        >
          <div class="tpl-editor-tour-tooltip-body">
            <div class="tpl-editor-tour-step-meta">
              {{
                format(t.tour.stepCounter, {
                  current: String(stepIndex + 1),
                  total: String(activeSteps.length),
                })
              }}
            </div>
            <div class="tpl-editor-tour-title">
              {{ currentStepTitle }}
            </div>
            <div class="tpl-editor-tour-text">
              {{ typedText
              }}<span
                v-if="typedText.length < currentStepText.length"
                class="tpl-editor-tour-cursor"
                >|</span
              >
            </div>
          </div>
          <div class="tpl-editor-tour-footer">
            <button
              type="button"
              data-testid="editor-tour-skip"
              class="tpl-editor-tour-skip"
              @click="dismiss(true)"
            >
              {{ t.tour.skip }}
            </button>
            <button
              type="button"
              data-testid="editor-tour-next"
              class="tpl-editor-tour-next"
              @click="nextStep"
            >
              {{
                stepIndex < activeSteps.length - 1 ? t.tour.next : t.tour.done
              }}
              <ChevronRight
                v-if="stepIndex < activeSteps.length - 1"
                :size="12"
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style>
/* Playground-aligned palette: Teleport targets `body`, outside `.tpl` theme vars. */

.tpl-editor-tour-overlay {
  --tour-primary: oklch(70% 0.16 55);
  --tour-primary-hover: oklch(63% 0.17 55);
  --tour-surface: #ffffff;
  --tour-title: #111827;
  --tour-body: #6b7280;
  --tour-skip: #9ca3af;
  --tour-skip-hover: #4b5563;
  --tour-border: #e5e7eb;
  --tour-footer-bg: rgb(249 250 251 / 0.5);
  --tour-next-fg: #ffffff;
  --tour-shadow-card: 0 16px 48px rgb(0 0 0 / 0.15);
}

.tpl-editor-tour-overlay--dark {
  --tour-primary: oklch(78% 0.14 55);
  --tour-primary-hover: oklch(70% 0.16 55);
  --tour-surface: #1f2937;
  --tour-title: #f9fafb;
  --tour-body: #9ca3af;
  --tour-skip: #6b7280;
  --tour-skip-hover: #d1d5db;
  --tour-border: #374151;
  --tour-footer-bg: rgb(31 41 55 / 0.5);
  --tour-next-fg: #ffffff;
  --tour-shadow-card: 0 16px 48px rgb(0 0 0 / 0.35);
}

.tpl-editor-tour-overlay:focus {
  outline: none;
}

.tpl-editor-tour-enter-active,
.tpl-editor-tour-leave-active {
  transition: opacity 200ms ease;
}

.tpl-editor-tour-enter-from,
.tpl-editor-tour-leave-to {
  opacity: 0;
}

/* Same ring/glow as legacy playground `.pg-onboarding-spotlight` */
.tpl-editor-tour-spotlight {
  box-shadow:
    0 0 0 2px oklch(70% 0.16 55 / 0.5),
    0 0 20px 6px oklch(70% 0.16 55 / 0.12),
    0 0 0 9999px rgba(0, 0, 0, 0.5);
  transition:
    transform 500ms cubic-bezier(0.4, 0, 0.2, 1),
    border-radius 500ms cubic-bezier(0.4, 0, 0.2, 1),
    opacity 400ms ease;
  pointer-events: auto;
}

@keyframes tpl-editor-tour-tooltip-in {
  from {
    opacity: 0;
    transform: translateY(10px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.tpl-editor-tour-tooltip-shell {
  width: 300px;
  overflow: hidden;
  border-radius: 12px;
  border: 1px solid var(--tour-border);
  background-color: var(--tour-surface);
  box-shadow: var(--tour-shadow-card);
  font-family:
    ui-sans-serif,
    system-ui,
    -apple-system,
    Segoe UI,
    Roboto,
    Helvetica,
    Arial,
    sans-serif;
  animation: tpl-editor-tour-tooltip-in 400ms cubic-bezier(0.4, 0, 0.2, 1) both;
}

.tpl-editor-tour-tooltip-body {
  padding: 16px 16px 12px;
}

.tpl-editor-tour-step-meta {
  margin-bottom: 4px;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--tour-primary);
}

.tpl-editor-tour-title {
  margin-bottom: 6px;
  font-size: 15px;
  font-weight: 600;
  color: var(--tour-title);
}

.tpl-editor-tour-text {
  min-height: 40px;
  font-size: 13px;
  line-height: 1.625;
  color: var(--tour-body);
}

.tpl-editor-tour-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-top: 1px solid var(--tour-border);
  background-color: var(--tour-footer-bg);
}

.tpl-editor-tour-skip {
  cursor: pointer;
  border: none;
  background: transparent;
  padding: 0;
  font-size: 13px;
  font-family: inherit;
  color: var(--tour-skip);
  transition: color 150ms ease;
}

.tpl-editor-tour-skip:hover {
  color: var(--tour-skip-hover);
}

.tpl-editor-tour-next {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  cursor: pointer;
  border: none;
  border-radius: 6px;
  padding: 0 16px;
  font-size: 13px;
  font-weight: 500;
  font-family: inherit;
  color: var(--tour-next-fg);
  background-color: var(--tour-primary);
  transition:
    background-color 150ms ease,
    box-shadow 150ms ease;
}

.tpl-editor-tour-next:hover {
  background-color: var(--tour-primary-hover);
  box-shadow:
    0 0 0 3px oklch(70% 0.16 55 / 0.15),
    0 4px 16px oklch(70% 0.16 55 / 0.25);
}

.tpl-editor-tour-overlay--dark .tpl-editor-tour-next:hover {
  box-shadow:
    0 0 0 3px oklch(78% 0.14 55 / 0.2),
    0 4px 16px oklch(70% 0.16 55 / 0.2);
}

@keyframes tpl-editor-tour-cursor-blink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
}

.tpl-editor-tour-cursor {
  animation: tpl-editor-tour-cursor-blink 800ms ease-in-out infinite;
  font-weight: 300;
  color: oklch(70% 0.16 55);
}

.tpl-editor-tour-overlay--dark .tpl-editor-tour-cursor {
  color: oklch(78% 0.14 55);
}

@media (prefers-reduced-motion: reduce) {
  .tpl-editor-tour-spotlight {
    transition-duration: 0.01ms !important;
  }

  .tpl-editor-tour-tooltip-shell {
    animation-duration: 0.01ms !important;
  }

  .tpl-editor-tour-cursor {
    animation: none !important;
  }
}
</style>
